import { Color, type Object3D, Vector3 } from 'three'
import { CANT_MOVE_STYLE, createWorldText, type WorldText } from './worldText'

/**
 * `BusController.cantMoveIndicator` — the label that floats over a bus the road
 * cannot take.
 *
 * 🔍 The tween is `BusController.AnimateCantMoveIndicator` (`__0x2106f80.c`),
 * which the compiler inlined into `OnBoxClicked` (`__0x21066dc.c`:216-327):
 *
 *     _cantMoveSequence?.Kill(true);
 *     _cantMoveSequence = DOTween.Sequence();
 *     t.position   = bus.position + Vector3.up * 20f;
 *     t.localScale = Vector3.one * 0.1f;
 *     cantMoveIndicator.color = Color.white;
 *     cantMoveIndicator.gameObject.SetActive(true);
 *     seq.Join(t.DOLocalMoveZ(t.localPosition.z + 4f, 0.40201f).SetEase(...));
 *     seq.Join(t.DOScale(Vector3.one * 0.45f,          0.22300f).SetEase(...));
 *     seq.Join(cantMoveIndicator.DOColor(targetColor,  0.22300f).SetEase(...));
 *     seq.Append(t.DOScale(Vector3.zero, 0f).SetEase(...));
 *     seq.OnComplete(() => cantMoveIndicator.gameObject.SetActive(false));
 *
 * The two durations are `0x3ecdd2f2` and `0x3e645a1d`; the rise is `+4` on the
 * label's local Z, and the label's rect is `m_SizeDelta 20 x 5` with the scene's
 * own `m_LocalScale 0.35` — which the tween overwrites, so 0.45 is absolute.
 *
 * ## What is not readable
 *
 *  - ❓ **The two strings.** `OnBoxClicked` picks between them on
 *    `current < max`; both are literals Ghidra drops, and the clip never shows
 *    the label. The placeholders below are declared as such.
 *  - ❓ **`cantMoveIndicatorTargetColor`** (0x58) is a `[SerializeField] Color`,
 *    stripped with the component. Red is the placeholder.
 *  - ❓ Every `SetEase` argument, as everywhere else.
 */

/** `0x3ecdd2f2`. */
const RISE_SECONDS = 0.40201
/** `0x3e645a1d`. */
const GROW_SECONDS = 0.223
const START_SCALE = 0.1
const END_SCALE = 0.45
/** `Vector3.up * 20f`, the height the label is parked at before it rises. */
const SPAWN_HEIGHT = 20
/** `DOLocalMoveZ(z + 4f)`. */
const RISE_DISTANCE = 4

/**
 * ❓ PLACEHOLDER — the two strings the original writes into the label.
 *
 * `OnBoxClicked` chooses `LENGTH` when the count still leaves room (so the road
 * is full by length) and `COUNT` otherwise. Nothing in the repo can supply the
 * words: they live in `global-metadata.dat`, and `reference/gameplay.mp4` has no
 * blocked tap in its 30 seconds. Recorded in `GAMEPLAY-CHECKLIST.md` §10.
 */
export const CANT_MOVE_TEXT = {
  /** `current < max` — room by count, none by length. */
  length: 'NO SPACE',
  /** The road is full by count. */
  count: 'ROAD FULL',
}

/** ❓ PLACEHOLDER — `cantMoveIndicatorTargetColor`, a stripped `[SerializeField]`. */
const TARGET_COLOR = new Color().setRGB(1, 0.25, 0.2, 'srgb')
const WHITE = new Color(1, 1, 1)

const _colour = new Color()

export class CantMoveIndicator {
  private readonly label: WorldText | null
  /** Seconds into the sequence, or null when the label is switched off. */
  private time: number | null = null
  private readonly origin = new Vector3()

  constructor(parent: Object3D) {
    this.label = createWorldText({ width: 20, height: 5, style: CANT_MOVE_STYLE })
    if (this.label) parent.add(this.label.object)
  }

  /**
   * The whole of `AnimateCantMoveIndicator`'s opening: park the label over the
   * bus, white and small, and start the sequence. `Kill(true)` on a sequence
   * already running means a second refused tap restarts it rather than queueing.
   */
  play(busPosition: Vector3, spaceMaxed: boolean): void {
    if (!this.label) return
    this.label.setText(spaceMaxed ? CANT_MOVE_TEXT.length : CANT_MOVE_TEXT.count)
    this.label.setColor(WHITE)
    this.origin.copy(busPosition).setY(busPosition.y + SPAWN_HEIGHT)
    this.label.object.position.copy(this.origin)
    this.label.object.scale.setScalar(START_SCALE)
    this.label.object.visible = true
    this.time = 0
  }

  sync(dt: number): void {
    if (!this.label || this.time === null) return
    this.time += dt

    // `Join`, so the three run together off one clock; `Append`ing the collapse
    // to zero is what ends it the moment the longest of them lands.
    const rise = Math.min(1, this.time / RISE_SECONDS)
    const grow = Math.min(1, this.time / GROW_SECONDS)

    this.label.object.position.z = this.origin.z + RISE_DISTANCE * rise
    this.label.object.scale.setScalar(START_SCALE + (END_SCALE - START_SCALE) * grow)
    this.label.setColor(_colour.copy(WHITE).lerp(TARGET_COLOR, grow))

    if (this.time >= RISE_SECONDS) {
      // `Append(DOScale(Vector3.zero, 0f))` then `OnComplete(SetActive(false))`.
      this.time = null
      this.label.object.visible = false
      this.label.object.scale.setScalar(0)
    }
  }

  dispose(): void {
    this.label?.object.removeFromParent()
    this.label?.dispose()
  }

  /** Test seam. */
  get visible(): boolean {
    return this.label?.object.visible ?? false
  }
}
